/** @jsxFrag React.Fragment */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';

import { Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';
import { RegionalPromotionFullWidthBanner } from 'bundles/front-page/components/banners/regional-promotion-banner/RegionalPromotionFullWidthBanner';
import ReactPriceDisplay from 'bundles/payments-common/components/ReactPriceDisplay';

import _t from 'i18n!nls/front-page';

const styles = {
  link: css`
    text-decoration: underline;
  `,
};

type Props = {
  variant?: 'blue' | 'purple' | 'light-blue';
};

const RegionalPromotionBanner = ({ variant }: Props) => {
  const track = useTracker();

  const onClick = (url: string) => () => {
    track('click_button', {
      button: {
        name: 'promo_cta',
        linkType: 'internal',
        linkURL: url,
      },
      pageSection: {
        sectionName: 'promo_bar',
      },
    });
  };

  const getBannerContent = () => {
    if (GrowthDiscoveryExperiments.get('regional2025MarchPromoGlobal')) {
      const link = '/courseraplus/special/spring-2025-global-25-monthly';
      return (
        <Typography2 component="p" variant="subtitleMedium">
          <FormattedMessage
            message={_t(
              `Cultivate your career with expert-led programs, job-ready certificates, and {numberOfWays} ways to grow. All for {price}/month, billed annually. {claim}`
            )}
            numberOfWays={<FormattedNumber value={10000} />}
            price={<ReactPriceDisplay value={25} currency="USD" hideCurrencyCode />}
            claim={
              <a css={styles.link} href={link} onClick={onClick(link)}>
                {_t('Save now')}
              </a>
            }
          />
        </Typography2>
      );
    }
    if (GrowthDiscoveryExperiments.get('regional2025MarchPromoGermany')) {
      const link = '/courseraplus/special/spring-de-25';
      return (
        <Typography2 component="p" variant="subtitleMedium">
          <FormattedMessage
            message={_t(`Expert-led. AI-translated. Now {price}/month, billed annually. {claim}`)}
            price={<ReactPriceDisplay value={25} currency="EUR" hideCurrencyCode />}
            claim={
              <a css={styles.link} href={link} onClick={onClick(link)}>
                {_t('Save now')}
              </a>
            }
          />
        </Typography2>
      );
    }

    if (GrowthDiscoveryExperiments.get('regional2025MarchPromoFrance')) {
      const link = '/courseraplus/special/spring-fr-25';
      return (
        <Typography2 component="p" variant="subtitleMedium">
          <FormattedMessage
            message={_t(`Expert-led. AI-translated. Now {price}/month, billed annually. {claim}`)}
            price={<ReactPriceDisplay value={25} currency="EUR" hideCurrencyCode />}
            claim={
              <a css={styles.link} href={link} onClick={onClick(link)}>
                {_t('Save now')}
              </a>
            }
          />
        </Typography2>
      );
    }

    if (GrowthDiscoveryExperiments.get('regional2025MarchPromoIndia')) {
      const showEarlyBird = GrowthDiscoveryExperiments.get('regional2025MarchPromoIndiaEarlyBird');
      const link = '/courseraplus/special/march-in-25';

      if (showEarlyBird) {
        return (
          <Typography2 component="p" variant="subtitleMedium">
            <FormattedMessage
              message={_t(
                `Paint your dream career with Coursera Plus. Get access to {numberOfWays}+ learning programs for {promoPrice}/year. {claim}`
              )}
              numberOfWays={<FormattedNumber value={10000} />}
              promoPrice={<ReactPriceDisplay value={7499} currency="INR" hideCurrencyCode />}
              claim={
                <a css={styles.link} href={link} onClick={onClick(link)}>
                  {_t('Save now')}
                </a>
              }
            />
          </Typography2>
        );
      }

      return (
        <Typography2 component="p" variant="subtitleMedium">
          <FormattedMessage
            message={_t(
              `Cultivate your career with expert-led programs, job-ready certificates, and {numberOfWays} ways to grow. All for {promoPrice}, down from {price}. {claim}`
            )}
            numberOfWays={<FormattedNumber value={10000} />}
            promoPrice={<ReactPriceDisplay value={8499} currency="INR" hideCurrencyCode />}
            price={<ReactPriceDisplay value={33000} currency="INR" hideCurrencyCode />}
            claim={
              <a css={styles.link} href={link} onClick={onClick(link)}>
                {_t('Save now')}
              </a>
            }
          />
        </Typography2>
      );
    }

    if (GrowthDiscoveryExperiments.get('regional2025MarchPromoEmergingMarkets')) {
      const link = '/courseraplus/special/march-offer-em';
      return (
        <Typography2 component="p" variant="subtitleMedium">
          <FormattedMessage
            message={_t(
              `Expert-led programs, job-ready certificates, {numberOfWays}+ ways to grow. Cultivate your career today for 60% off. {claim}`
            )}
            numberOfWays={<FormattedNumber value={10000} />}
            claim={
              <a css={styles.link} href={link} onClick={onClick(link)}>
                {_t('Save now')}
              </a>
            }
          />
        </Typography2>
      );
    }

    if (GrowthDiscoveryExperiments.get('regional2025MarchPromoLatam')) {
      const link = '/courseraplus/special/march-offer-latam';
      return (
        <Typography2 component="p" variant="subtitleMedium">
          <FormattedMessage
            message={_t(`Unlock expert-led, AI-translated courses for 60% off. {claim}`)}
            claim={
              <a css={styles.link} href={link} onClick={onClick(link)}>
                {_t('Save now')}
              </a>
            }
          />
        </Typography2>
      );
    }
    if (GrowthDiscoveryExperiments.get('regional2025MarchPromoSpain')) {
      const link = '/courseraplus/special/spring-es-25';
      return (
        <Typography2 component="p" variant="subtitleMedium">
          <FormattedMessage
            message={_t(`Expert-led. AI-translated. Now {price}/month, billed annually. {claim}`)}
            price={<ReactPriceDisplay value={25} currency="EUR" hideCurrencyCode />}
            claim={
              <a css={styles.link} href={link} onClick={onClick(link)}>
                {_t('Save on Coursera Plus')}
              </a>
            }
          />
        </Typography2>
      );
    }
    return null;
  };

  const bannerContent = getBannerContent();

  if (!bannerContent) {
    return null;
  }

  // This div wrapper is required.
  // This wrapper prevents some sort of hydration related bug which leaks into the hero section below.
  return (
    <div>
      <RegionalPromotionFullWidthBanner variant={variant}>{getBannerContent()}</RegionalPromotionFullWidthBanner>
    </div>
  );
};

export { RegionalPromotionBanner };

export default RegionalPromotionBanner;
